/**
 * ScrapeKing Design System v2 - Layout Components
 * Flex, Grid, Box, Stack, Divider utilities
 * "Data Extraction Terminal" Aesthetic
 */

import styled, { css } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// BOX - Basic container with common styling props
// ═══════════════════════════════════════════════════════════════

interface BoxProps {
  $p?: number | string;
  $px?: number | string;
  $py?: number | string;
  $pt?: number | string;
  $pb?: number | string;
  $pl?: number | string;
  $pr?: number | string;
  $m?: number | string;
  $mx?: number | string;
  $my?: number | string;
  $mt?: number | string;
  $mb?: number | string;
  $ml?: number | string;
  $mr?: number | string;
  $bg?: string;
  $rounded?: boolean | string;
  $border?: boolean;
}

const getSpacing = (value: number | string | undefined): string => {
  if (value === undefined) return '';
  if (typeof value === 'number') return `var(--sk-space-${value})`;
  return value;
};

export const Box = styled.div<BoxProps>`
  ${props => props.$p && `padding: ${getSpacing(props.$p)};`}
  ${props => props.$px && `padding-left: ${getSpacing(props.$px)}; padding-right: ${getSpacing(props.$px)};`}
  ${props => props.$py && `padding-top: ${getSpacing(props.$py)}; padding-bottom: ${getSpacing(props.$py)};`}
  ${props => props.$pt && `padding-top: ${getSpacing(props.$pt)};`}
  ${props => props.$pb && `padding-bottom: ${getSpacing(props.$pb)};`}
  ${props => props.$pl && `padding-left: ${getSpacing(props.$pl)};`}
  ${props => props.$pr && `padding-right: ${getSpacing(props.$pr)};`}
  ${props => props.$m && `margin: ${getSpacing(props.$m)};`}
  ${props => props.$mx && `margin-left: ${getSpacing(props.$mx)}; margin-right: ${getSpacing(props.$mx)};`}
  ${props => props.$my && `margin-top: ${getSpacing(props.$my)}; margin-bottom: ${getSpacing(props.$my)};`}
  ${props => props.$mt && `margin-top: ${getSpacing(props.$mt)};`}
  ${props => props.$mb && `margin-bottom: ${getSpacing(props.$mb)};`}
  ${props => props.$ml && `margin-left: ${getSpacing(props.$ml)};`}
  ${props => props.$mr && `margin-right: ${getSpacing(props.$mr)};`}
  ${props => props.$bg && `background: ${props.$bg};`}
  ${props => props.$rounded && (typeof props.$rounded === 'string'
    ? `border-radius: var(--sk-radius-${props.$rounded});`
    : `border-radius: var(--sk-radius-default);`
  )}
  ${props => props.$border && `border: 1px solid var(--sk-surface-border);`}
`;

// ═══════════════════════════════════════════════════════════════
// FLEX - Flexbox container
// ═══════════════════════════════════════════════════════════════

interface FlexProps extends BoxProps {
  $direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  $align?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  $justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  $wrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  $gap?: number | string;
  $flex?: string | number;
}

export const Flex = styled(Box)<FlexProps>`
  display: flex;
  ${props => props.$direction && `flex-direction: ${props.$direction};`}
  ${props => props.$align && `align-items: ${props.$align};`}
  ${props => props.$justify && `justify-content: ${props.$justify};`}
  ${props => props.$wrap && `flex-wrap: ${props.$wrap};`}
  ${props => props.$gap && `gap: ${getSpacing(props.$gap)};`}
  ${props => props.$flex && `flex: ${props.$flex};`}
`;

// ═══════════════════════════════════════════════════════════════
// STACK - Vertical or horizontal stack with consistent spacing
// ═══════════════════════════════════════════════════════════════

interface StackProps extends BoxProps {
  $direction?: 'horizontal' | 'vertical';
  $gap?: number | string;
  $align?: 'flex-start' | 'flex-end' | 'center' | 'stretch';
}

export const Stack = styled(Box)<StackProps>`
  display: flex;
  flex-direction: ${props => props.$direction === 'horizontal' ? 'row' : 'column'};
  gap: ${props => getSpacing(props.$gap || 4)};
  ${props => props.$align && `align-items: ${props.$align};`}
`;

// ═══════════════════════════════════════════════════════════════
// GRID - CSS Grid container
// ═══════════════════════════════════════════════════════════════

interface GridProps extends BoxProps {
  $columns?: number | string;
  $rows?: string;
  $gap?: number | string;
  $rowGap?: number | string;
  $colGap?: number | string;
}

export const Grid = styled(Box)<GridProps>`
  display: grid;
  ${props => {
    if (typeof props.$columns === 'number') {
      return `grid-template-columns: repeat(${props.$columns}, minmax(0, 1fr));`;
    }
    return props.$columns && `grid-template-columns: ${props.$columns};`;
  }}
  ${props => props.$rows && `grid-template-rows: ${props.$rows};`}
  ${props => props.$gap && `gap: ${getSpacing(props.$gap)};`}
  ${props => props.$rowGap && `row-gap: ${getSpacing(props.$rowGap)};`}
  ${props => props.$colGap && `column-gap: ${getSpacing(props.$colGap)};`}
`;

// ═══════════════════════════════════════════════════════════════
// DIVIDER
// ═══════════════════════════════════════════════════════════════

interface DividerProps {
  $orientation?: 'horizontal' | 'vertical';
  $variant?: 'solid' | 'dashed' | 'gradient';
  $my?: number;
  $mx?: number;
}

export const Divider = styled.div<DividerProps>`
  ${props => props.$orientation === 'vertical' ? css`
    width: 1px;
    height: 100%;
    min-height: 20px;
    margin: 0 ${getSpacing(props.$mx || 4)};
  ` : css`
    height: 1px;
    width: 100%;
    margin: ${getSpacing(props.$my || 4)} 0;
  `}

  ${props => {
    switch (props.$variant) {
      case 'dashed':
        return css`
          background: transparent;
          border: none;
          background-image: linear-gradient(
            ${props.$orientation === 'vertical' ? 'to bottom' : 'to right'},
            var(--sk-surface-border) 50%,
            transparent 50%
          );
          background-size: ${props.$orientation === 'vertical' ? '1px 8px' : '8px 1px'};
        `;
      case 'gradient':
        return css`
          background: linear-gradient(
            ${props.$orientation === 'vertical' ? 'to bottom' : 'to right'},
            transparent,
            var(--sk-cyan-700),
            transparent
          );
        `;
      default:
        return css`
          background: var(--sk-surface-border);
        `;
    }
  }}
`;

// ═══════════════════════════════════════════════════════════════
// TYPOGRAPHY
// ═══════════════════════════════════════════════════════════════

export const Text = styled.p<{
  $size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  $weight?: 400 | 500 | 600 | 700;
  $color?: 'primary' | 'secondary' | 'tertiary' | 'cyan' | 'green' | 'amber' | 'red';
  $mono?: boolean;
  $truncate?: boolean;
}>`
  margin: 0;
  font-family: ${props => props.$mono ? 'var(--sk-font-mono)' : 'var(--sk-font-body)'};
  font-size: var(--sk-text-${props => props.$size || 'base'});
  font-weight: ${props => props.$weight || 400};
  color: ${props => {
    switch (props.$color) {
      case 'secondary': return 'var(--sk-text-secondary)';
      case 'tertiary': return 'var(--sk-text-tertiary)';
      case 'cyan': return 'var(--sk-cyan-500)';
      case 'green': return 'var(--sk-green-500)';
      case 'amber': return 'var(--sk-amber-500)';
      case 'red': return 'var(--sk-red-500)';
      default: return 'var(--sk-text-primary)';
    }
  }};
  ${props => props.$truncate && css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const Heading = styled.h2<{
  $level?: 1 | 2 | 3 | 4 | 5 | 6;
  $gradient?: boolean;
}>`
  margin: 0;
  font-family: var(--sk-font-display);
  font-weight: 700;
  color: var(--sk-text-primary);
  letter-spacing: -0.02em;

  ${props => {
    switch (props.$level || 2) {
      case 1: return 'font-size: var(--sk-text-4xl);';
      case 2: return 'font-size: var(--sk-text-3xl);';
      case 3: return 'font-size: var(--sk-text-2xl);';
      case 4: return 'font-size: var(--sk-text-xl);';
      case 5: return 'font-size: var(--sk-text-lg);';
      case 6: return 'font-size: var(--sk-text-base);';
    }
  }}

  ${props => props.$gradient && css`
    background: linear-gradient(
      135deg,
      var(--sk-text-primary) 0%,
      var(--sk-text-primary) 70%,
      var(--sk-cyan-400) 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `}
`;

// ═══════════════════════════════════════════════════════════════
// TOOLTIP
// ═══════════════════════════════════════════════════════════════

export const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;

  &:hover [data-tooltip] {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
`;

export const TooltipContent = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  padding: var(--sk-space-2) var(--sk-space-3);
  background: var(--sk-bg-elevated);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-sm);
  font-size: var(--sk-text-xs);
  color: var(--sk-text-primary);
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1000;
  box-shadow: var(--sk-shadow-lg);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: var(--sk-surface-border);
  }
`;

// ═══════════════════════════════════════════════════════════════
// CHIP (like MUI Chip)
// ═══════════════════════════════════════════════════════════════

export const Chip = styled.span<{
  $variant?: 'default' | 'cyan' | 'green' | 'amber' | 'red';
  $size?: 'sm' | 'md';
  $clickable?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-2);
  padding: ${props => props.$size === 'sm' ? 'var(--sk-space-1) var(--sk-space-2)' : 'var(--sk-space-2) var(--sk-space-3)'};
  font-family: var(--sk-font-mono);
  font-size: ${props => props.$size === 'sm' ? 'var(--sk-text-2xs)' : 'var(--sk-text-xs)'};
  font-weight: 500;
  border-radius: var(--sk-radius-full);
  white-space: nowrap;

  ${props => {
    switch (props.$variant) {
      case 'cyan':
        return css`
          background: rgba(0, 200, 224, 0.1);
          color: var(--sk-cyan-400);
          border: 1px solid var(--sk-cyan-700);
        `;
      case 'green':
        return css`
          background: rgba(0, 255, 127, 0.1);
          color: var(--sk-green-400);
          border: 1px solid var(--sk-green-700);
        `;
      case 'amber':
        return css`
          background: rgba(255, 184, 0, 0.1);
          color: var(--sk-amber-400);
          border: 1px solid var(--sk-amber-700);
        `;
      case 'red':
        return css`
          background: rgba(255, 0, 51, 0.1);
          color: var(--sk-red-400);
          border: 1px solid var(--sk-red-700);
        `;
      default:
        return css`
          background: var(--sk-surface-default);
          color: var(--sk-text-secondary);
          border: 1px solid var(--sk-surface-border);
        `;
    }
  }}

  ${props => props.$clickable && css`
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      filter: brightness(1.1);
    }
  `}

  svg {
    width: 14px;
    height: 14px;
  }
`;

export default { Box, Flex, Stack, Grid, Divider, Text, Heading, TooltipWrapper, TooltipContent, Chip };
